/* eslint-disable @typescript-eslint/no-unnecessary-qualifier */
import { Enum, Num, Obj } from '@livecontrol/core-utils';
import type { DateTime, Duration } from 'luxon';
import type { Asset } from './asset';
import type { Destination } from './destination';
import { EventType as EventType_ } from './event-types';
import type { EventFeedback } from './feedback';
import type { Location } from './location';
import type { Slide } from './slide';

export interface Event {
  id: Event.ID;
  assets?: Asset[];
  description?: string;
  backgroundURL?: string;
  destinations?: Destination[];
  extraCharges?: Event.ExtraCharge[];
  integrations?: string[];
  end: DateTime;
  location: Location;
  eventFeedback?: EventFeedback;
  operatorNotes?: string;
  password?: string;
  requireRegistration: boolean;
  showViewers: boolean;
  registration: boolean;
  showGuestbook: boolean;
  liveChat: boolean;
  redirectUrl?: string;
  phase: Event.Phase;
  private: boolean;
  production: Event.Production;
  recordOnly: boolean;
  repeat?: Event.Repeat;
  slides?: Slide[];
  clientContactDetails?: Event.ClientContactDetails;
  clientContacts?: Event.ClientContactDetails[];
  simulatedLiveDetails?: Event.SimulatedLiveDetails;
  start: DateTime;
  title: string;
  zoomDetails?: Event.ZoomDetails;
  cueSheetURL?: string;
  audioCueSheetURL?: string;
  type: string; // 'cb1' | 'cb2' | 'cb3' | 'regular';
  redirectUrlSettings?: Event.RedirectUrlSettings;
  mobileKitData?: Event.MobileKitData;
  isMobileKit?: boolean;
  receiveIsoRecordings?: boolean;
  useIntroAndOutroSlides?: boolean;
  technicalContactSettings?: Event.TechnicalContactSettings;
  contactSettings?: Event.ContactSettings;
  syncContactSettings?: Event.ContactSettings;
  linkedEventId?: Event.ID;
  linkedEventInfo?: Event;
  automaticZoom?: boolean;
  recurrenceId?: string;
}

export namespace Event {
  export type ID = number;
  export type Like = Event | ID | string;

  export const toId = (event?: Like): ID | undefined =>
    Num.normalize(Obj.isObject<Event>(event) ? event.id : event, { positive: true });

  export interface Events {
    events?: Event[];
    hasMore?: boolean;
    page?: number;
  }

  export const Constraints = {
    DESCRIPTION_MAX_LENGTH: 5e3,
    MAX_DESTINATIONS: 4,
    NOTES_MAX_LENGTH: 255,
    TITLE_MAX_LENGTH: 255
  };

  export enum EventCategories {
    STATIC = 'Static',
    PRODUCED = 'Produced',
    SIMULATED = 'Simulated Live',
    TEST = 'Test',
    MOBILE_KIT = 'Mobile Kit',
    MOBILE = 'Mobile',
    EXTERNAL_ENCODER = 'External Encoder'
  }

  export namespace EventCategories {
    export const normalize = (record?: unknown): EventCategories | undefined => {
      if (record) {
        return EventCategories.EXTERNAL_ENCODER;
      }

      return undefined;
    };
  }


  export const calendarColors: Record<string, string> = {
    Produced: '#2E5BFF',
    Static: '#FF505F',
    Test: '#83D750',
    'Simulated Live': '#D4EF63',
    Mobile: '#EF9563',
    MobileKit: '#EF9563',
    'External Encoder': '#5E83FF'
  };

  export namespace EventTypeForFilters {
    export const keys = {
      Produced: 'Produced',
      Static: 'Static',
      Test: 'Test',
      Simulated: 'Simulated',
      Mobile: 'Mobile',
      MobileKit: 'Mobile Kit',
      External: 'External'
    };

    export type Key = keyof typeof keys;
  }

  export namespace EventTypeForScheduleFilters {
    export const keys = {
      Produced: 'Produced',
      Static: 'Static',
      Test: 'Test',
      Simulated: 'Simulated Live',
      Mobile: 'Mobile',
      External: 'External Encoder'
    };

    export type Key = keyof typeof keys;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  export import EventType = EventType_;

  export enum Production {
    Produced = 1,
    Static = 2,
    Simulated_Live = 3,
    Client_Booking = 4,
    Mobile = 5,
    Test = 6,
    Mobile_Kit = 7,
    External = 8
  }

  export interface ExtraCharge {
    type: string;
    amount: number;
  }

  export namespace Production {
    export const normalize = (value: unknown): Production | undefined =>
      Enum.normalize(Production, value);

    export const stringValues = new Map<Production, string>([
      [Production.Produced, 'Produced'],
      [Production.Static, 'Static'],
      [Production.Simulated_Live, 'Simulated Live'],
      [Production.Client_Booking, 'Client Booking'],
      [Production.Mobile, 'Mobile'],
      [Production.Test, 'Test'],
      [Production.External, 'External']
    ]);


    export const computeCreditsChargedMultiplier = ({
      duration,
      overTimeLimitInMinutes,
      overTimeLimitCharge
    }: {
      duration: Duration;
      overTimeLimitInMinutes: number;
      overTimeLimitCharge: number;
    }): number => {
      const creditsToCharge = duration.as('minutes') > overTimeLimitInMinutes ? overTimeLimitCharge : 1;

      return creditsToCharge;
    };


    export const toString = (production: Production): string => stringValues.get(production)!;
  }


  export enum Phase {
    Upcoming = 1,
    Live = 2,
    Past = 3
  }

  export namespace Phase {
    export const normalize = (value: unknown): Phase | undefined => Enum.normalize(Phase, value);
  }

  export enum Interval {
    Day = 1,
    Week = 2,
    Month = 3
  }

  export namespace Interval {
    export const normalize = (value: unknown): Interval | undefined =>
      Enum.normalize(Interval, value);
  }

  export enum DayOfWeek {
    Sunday = 1,
    Monday = 2,
    Tuesday = 3,
    Wednesday = 4,
    Thursday = 5,
    Friday = 6,
    Saturday = 7
  }

  export namespace DayOfWeek {
    export const normalize = (value: unknown): DayOfWeek | undefined =>
      Enum.normalize(DayOfWeek, value);
  }

  export interface Repeat {
    value: number;
    interval: Interval;
    days: DayOfWeek[];
    until?: Date | number;
  }

  export interface ClientContactDetails {
    name?: string | null;
    email?: string | null;
    phoneNumber?: string | null;
  }

  export interface ZoomDetails {
    streamZoom?: boolean;
    meetingId?: string;
    password?: string;
  }

  export interface SimulatedLiveDetails {
    fileName?: string;
    assetId?: number;
    assetUrl?: string;
  }

  export interface RedirectUrlSettings {
    enabled?: boolean;
    url?: string;
  }

  export interface MobileKitData {
    title?: string;
    description?: string;
    location?: string;
    start?: DateTime;
    end?: DateTime;
  }

  export interface TechnicalContactSettings {
    enabled?: boolean;
    contactId?: number;
  }

  export interface ContactSettings {
    enabled?: boolean;
    contactId?: number;
  }
}
